import {Component, OnDestroy, OnInit} from '@angular/core';
import {BmPaymentPlanService} from '../../service/bm-payment-plan.service';
import {DunningData} from '../../model/dunning-data.interface';
import {Contract} from '../../model/contract.interface';
import {Charge} from '../../model/charge.interface';
import {PaymentPlanResult} from '../../model/payment-plan-result.interface';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Popover} from "primeng/popover";

type BillingIcon = 'pi-arrow-left' | 'pi-arrow-right';

interface BillingAccountRowVm {
    label: string;           // e.g. "Rückstand"
    subLabel?: string;       // e.g. "(3 Monate)" or "BLR (Bank Fee)"
    amount: number;          // numeric, formatted in template
    currency: string;        // "EUR"
    icon?: BillingIcon;      // arrow icon
    iconTone?: 'neutral' | 'info' | 'danger';
    emphasize?: boolean;     // bold amount
    charge?: Charge;          // present only for charge rows
    removable?: boolean;      // UI hint
}

@Component({
    selector: 'bm-payment-plan-activity',
    templateUrl: './bm-payment-plan-activity.component.html',
    styleUrls: ['./bm-payment-plan-activity.component.scss'],
    standalone: false
})
export class BmPaymentPlanActivityComponent implements OnInit, OnDestroy {

    dunningData: DunningData | null = null;
    paymentPlanResult: PaymentPlanResult | null = null;

    // UI state for panels
    chargesPanelExpanded: boolean = true;
    contractsPanelExpanded: boolean = true;
    oneTimePaymentPanelExpanded: boolean = true;
    paymentSchedulePanelExpanded: boolean = true;

    // Contract activity tracking (which button is selected for each contract)
    contractActivities: Map<string, 'dunning' | 'reactivation' | null> = new Map();

    // One-time payment form
    showOneTimePaymentDialog: boolean = false;


    // Charge adjustment dialog
    showChargeAdjustmentDialog: boolean = false;
    selectedCharge: Charge | null = null;
    chargeAdjustmentType: 'remove' | 'reduce' = 'remove';
    reducedChargeAmount: number = 0;

    // Slider values
    maxPaybackAmount: number = 100;
    paybackAmountPerMonth: number = 10;

    selectedDate: Date = new Date();
    billingAccountRows: BillingAccountRowVm[] = [];

    private destroy$ = new Subject<void>();
    selectedContractForPriceView: Contract;
    durationMonths: number = 0;

    terminateAllChecked = false;
    reactivateAllChecked = false;

    constructor(private paymentPlanService: BmPaymentPlanService) {
    }

    ngOnInit(): void {
        this.subscribeToServiceUpdates();
    }

    /**
     * Subscribe to service updates
     */
    private subscribeToServiceUpdates(): void {
        // Subscribe to dunning data
        this.paymentPlanService.dunningData$
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.dunningData = data;
                if (data) {
                    this.initializeMaxPaybackAmount();
                    this.paymentPlanService.setPaybackAmountPerMonth(this.paybackAmountPerMonth);

                } else {
                    this.billingAccountRows = [];
                }
            });

        // Subscribe to payment plan result
        this.paymentPlanService.paymentPlanResult$
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                this.paymentPlanResult = result;

                if (result) {
                    this.durationMonths = result.durationMonths;
                    this.paybackAmountPerMonth = result.monthlyPaybackAmount;
                }
            });

        // Subscribe to payment plan to track contract activities
        this.paymentPlanService.paymentPlan$
            .pipe(takeUntil(this.destroy$))
            .subscribe(plan => {

                // 🔑 RESET first – this removes stale selections
                this.contractActivities.clear();


                if (plan) {
                    // Update contract activities map
                    plan.contracts.forEach(ca => {
                        const key = ca.activity === 'reactivate' ? 'reactivation' : 'dunning';
                        this.contractActivities.set(ca.contract.identifier, key);
                    });
                }

                this.syncBulkCheckboxes();
            });
    }

    /**
     * Initialize max payback amount from total charges and contract dunning amounts
     */
    private initializeMaxPaybackAmount(): void {
        if (!this.dunningData) return;

        let total = 0;

        // Add all charges
        this.dunningData.charges.forEach(charge => {
            total += charge.amount;
        });

        // Add all contract dunning amounts (initial debt)
        this.dunningData.contracts.forEach(contract => {
            if (contract.status === 'in-dunning') {
                total += contract.dunningAmount;
            }
        });

        this.maxPaybackAmount = Math.ceil(total);
        this.paybackAmountPerMonth = Math.min(10, this.maxPaybackAmount);
    }

    /**
     * Handle contract button click
     */
    async onContractButtonClick(contract: Contract, type: 'dunning' | 'reactivation'): Promise<void> {
        const currentActivity = this.contractActivities.get(contract.identifier);

        if (currentActivity === type) {
            // Already selected, remove it
            this.contractActivities.set(contract.identifier, null);
            await this.paymentPlanService.removeContractActivity(contract.identifier);
        } else {
            // Select this button
            this.contractActivities.set(contract.identifier, type);
            const activity = type === 'reactivation' ? 'reactivate' : 'will-cancel';
            await this.paymentPlanService.setContractActivity(contract, activity);
        }
    }

    /**
     * Check if contract button is selected
     */
    isContractButtonSelected(contract: Contract, type: 'dunning' | 'reactivation'): boolean {
        return this.contractActivities.get(contract.identifier) === type;
    }

    /**
     * Open charge adjustment dialog
     */
    openChargeAdjustmentDialog(charge: Charge): void {
        this.selectedCharge = charge;

        // Decide default adjustment type
        if (!charge.deductable && charge.reducable) {
            // Only reduce is possible → auto-select reduce
            this.chargeAdjustmentType = 'reduce';
        } else {
            // Default behavior
            this.chargeAdjustmentType = 'remove';
        }

        this.reducedChargeAmount = charge.amount;
        this.showChargeAdjustmentDialog = true;
    }

    /**
     * Apply charge adjustment
     */
    async applyChargeAdjustment(): Promise<void> {
        if (!this.selectedCharge) return;

        if (this.chargeAdjustmentType === 'remove') {
            await this.paymentPlanService.adjustCharge(this.selectedCharge, 'remove');
        } else if (this.chargeAdjustmentType === 'reduce') {
            await this.paymentPlanService.adjustCharge(
                this.selectedCharge,
                'reduce',
                this.reducedChargeAmount
            );


        }

        this.showChargeAdjustmentDialog = false;
    }

    /**
     * Check if charge is adjusted
     */
    isChargeAdjusted(charge: Charge): boolean {
        const adjustments = this.paymentPlanService['chargeAdjustmentsSubject'].value;
        return adjustments.some(adj => adj.charge.name === charge.name);
    }

    /**
     * Restore charge to original amount
     */
    async restoreCharge(charge: Charge): Promise<void> {
        await this.paymentPlanService.restoreCharge(charge);
    }


    /**
     * Show one-time payment dialog
     */
    openOneTimePaymentDialog(): void {
        this.showOneTimePaymentDialog = true;
    }



    /**
     * Handle slider change for payback amount
     */
    async onPaybackAmountChange(value: number): Promise<void> {
        this.paybackAmountPerMonth = value;
        await this.paymentPlanService.setPaybackAmountPerMonth(value);
    }

    async onDurationMonthsChange(months: number): Promise<void> {
        if (!months || months <= 0) return;

        await this.paymentPlanService.setDurationMonths(months);
    }

    /**
     * Get contracts that are not "new" status
     */
    getContractsInDunning(): Contract[] {
        return this.dunningData?.contracts.filter(c => c.status === 'in-dunning') || [];
    }

    /**
     * Get contracts with "new" status
     */
    getNewContracts(): Contract[] {
        return this.dunningData?.contracts.filter(c => c.status === 'new') || [];
    }


    // ---- Contract card helpers (UI only) ----

    getContractMonthlyPrice(contract: Contract): number {
        const last = contract.monthlyPrice?.[contract.monthlyPrice.length - 1];
        return last?.monthlyPrice ?? 0;
    }

    getContractBadge(contract: Contract): 'STORNO' | 'WIK' | 'NEU' | 'Keine Auswahl' {
        if (contract.status === 'new') return 'NEU';

        const act = this.contractActivities.get(contract.identifier);
        // selected dunning => STORNO, selected reactivation or default => WIK
        if (act === 'dunning') return 'STORNO';
        if (act === 'reactivation') return 'WIK';
        return 'Keine Auswahl';
    }

    getContractBadgeClass(contract: Contract): 'storno' | 'wik' | 'neu' | 'unselected' {
        if (contract.status === 'new') return 'neu';

        const act = this.contractActivities.get(contract.identifier);
        // selected dunning => STORNO, selected reactivation or default => WIK
        if (act === 'dunning') return 'storno';
        if (act === 'reactivation') return 'wik';
        return 'unselected';
    }

    getContractBadgeIcon(contract: Contract): string {
        if (contract.status === 'new') return '';

        const act = this.contractActivities.get(contract.identifier);
        // selected dunning => STORNO, selected reactivation or default => WIK
        if (act === 'dunning') return 'pi-exclamation-circle date-icon-warning text-red-600';
        if (act === 'reactivation') return 'pi-shield date-icon-ok';
        return 'pi-file date-icon-neutral';
    }

    getContractDateClass(contract: Contract): string {
        const act = this.contractActivities.get(contract.identifier);

        if (act === 'dunning') return 'line-through';
        return '';
    }


    // Which row is "active" (red arrow-left) vs "inactive" (gray arrow-right)
    isRowActive(contract: Contract, row: 'bisWik' | 'rueckstand'): boolean {
        if (contract.status === 'new') return false;

        const act = this.contractActivities.get(contract.identifier);

        // if user selected reactivation → Bis WIK is active
        if (act === 'reactivation') return row === 'bisWik';

        // if user selected dunning/terminate → Rückstand is active
        if (act === 'dunning') return row === 'rueckstand';

        // default (no selection): nothing active
        return false;
    }

    // WIK date: derived UI helper (you don't have this date in the model)
    // We compute "today + (dunningMonths + reactivationMonths) months", first day of that month.
    getWikDate(contract: Contract): string {
        if (contract.status === 'new') {
            // for new contracts show "Startdatum" based on monthlyPrice startMonth
            const sm = contract.monthlyPrice?.[0]?.startMonth; // YYYY-MM
            return this.formatYearMonthToDate(sm);
        }

        const months = (contract.dunningMonths ?? 0) + (contract.reactivationMonths ?? 0);
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() + months);

        return this.formatDate(d);
    }

    private formatYearMonthToDate(ym?: string): string {
        // ym = "2026-01" → 01.01.2026
        if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, m - 1, 1);
        return this.formatDate(d);
    }

    private formatDate(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    getMonthlyPrice(c: Contract): number {
        return c.monthlyPrice[c.monthlyPrice.length - 1]?.monthlyPrice ?? 0;
    }

    isFutureContract(c: Contract): boolean {
        const start = c.monthlyPrice[0]?.startMonth;
        if (!start) return false;
        return start > this.currentYearMonth();
    }

    formatYearMonth(ym?: string): string {
        if (!ym) return '';
        const [y, m] = ym.split('-');
        return `01.${m}.${y}`;
    }

    getEndDate(months: number): string {
        const d = new Date();
        d.setMonth(d.getMonth() + months);
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    }

    private currentYearMonth(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }

    getMonthlyRateContracts(): Contract[] {
        if (!this.dunningData) return [];

        return this.dunningData.contracts.filter(c => {
            // Always include new contracts
            if (c.status === 'new') {
                return true;
            }

            // Include only explicitly reactivated contracts
            return this.contractActivities.get(c.identifier) === 'reactivation';
        });
    }

    handleContractPriceView(pricePanel: Popover, event: MouseEvent, contract: Contract) {
        this.selectedContractForPriceView = contract;
        pricePanel.toggle(event);

    }

    async onMonthlyPaybackAmountCommit(value: number): Promise<void> {
        if (!value || value <= 0) return;

        await this.paymentPlanService.setPaybackAmountPerMonth(value);
    }

    async onDurationMonthsCommit(value: number): Promise<void> {
        if (!value || value <= 0) return;

        await this.paymentPlanService.setDurationMonths(value);
    }

    private getEligibleContracts(): Contract[] {
        return this.dunningData?.contracts.filter(c => c.status !== 'new') || [];
    }

    async onTerminateAllToggle(checked: boolean): Promise<void> {
        if (!checked) {
            // Unselect all terminations
            await this.clearAllContractActivities();
            return;
        }

        // Mutual exclusivity
        this.reactivateAllChecked = false;

        const contracts = this.getEligibleContracts();

        for (const contract of contracts) {
            this.contractActivities.set(contract.identifier, 'dunning');
            await this.paymentPlanService.setContractActivity(contract, 'will-cancel');
        }
    }

    async onReactivateAllToggle(checked: boolean): Promise<void> {
        if (!checked) {
            // Unselect all reactivations
            await this.clearAllContractActivities();
            return;
        }

        // Mutual exclusivity
        this.terminateAllChecked = false;

        const contracts = this.getEligibleContracts();

        for (const contract of contracts) {
            this.contractActivities.set(contract.identifier, 'reactivation');
            await this.paymentPlanService.setContractActivity(contract, 'reactivate');
        }
    }

    private async clearAllContractActivities(): Promise<void> {
        const contracts = this.getEligibleContracts();

        for (const contract of contracts) {
            this.contractActivities.set(contract.identifier, null);
            await this.paymentPlanService.removeContractActivity(contract.identifier);
        }
    }

    private syncBulkCheckboxes(): void {
        const contracts = this.getEligibleContracts();
        if (!contracts.length) {
            this.terminateAllChecked = false;
            this.reactivateAllChecked = false;
            return;
        }

        const allTerminated = contracts.every(
            c => this.contractActivities.get(c.identifier) === 'dunning'
        );

        const allReactivated = contracts.every(
            c => this.contractActivities.get(c.identifier) === 'reactivation'
        );

        this.terminateAllChecked = allTerminated;
        this.reactivateAllChecked = allReactivated;
    }

    get isReduceAmountInvalid(): boolean {
        if (this.chargeAdjustmentType !== 'reduce') {
            return false;
        }

        return (
            !this.reducedChargeAmount ||
            this.reducedChargeAmount <= 0 ||
            this.reducedChargeAmount > this.selectedCharge!.amount
        );
    }

    get isChargeAdjustmentValid(): boolean {
        if (!this.selectedCharge) return false;

        if (this.chargeAdjustmentType === 'remove') {
            return this.selectedCharge.deductable === true;
        }

        if (this.chargeAdjustmentType === 'reduce') {
            return !this.isReduceAmountInvalid;
        }

        return false;
    }

    selectContract(contract: Contract): void {
        this.paymentPlanService.setSelectedContract(contract);
    }

    getContractActionOptions(contract: Contract) {
        return [
            {
                label: `Terminate (€${contract.dunningAmount.toFixed(2)})`,
                value: 'dunning',
                severity: 'danger'
            },
            {
                label: `Reactivate (€${contract.reactivationAmount.toFixed(2)})`,
                value: 'reactivation',
                severity: 'success'
            }
        ];
    }

    async onContractActionSelect(
        contract: Contract,
        value: 'dunning' | 'reactivation' | null
    ): Promise<void> {

        const current = this.contractActivities.get(contract.identifier);

        // Deselection
        if (!value || current === value) {
            this.contractActivities.set(contract.identifier, null);
            await this.paymentPlanService.removeContractActivity(contract.identifier);
            return;
        }

        // New selection
        this.contractActivities.set(contract.identifier, value);

        const activity = value === 'reactivation'
            ? 'reactivate'
            : 'will-cancel';

        await this.paymentPlanService.setContractActivity(contract, activity);
    }

    isChargeActionDisabled(charge: Charge): boolean {
        return !charge.deductable && !charge.reducable;
    }

    /**
     * True = charge exists in balance entries
     */
    isChargeInBalance(charge: Charge): boolean {
        const adjustments = this.paymentPlanService['chargeAdjustmentsSubject'].value;

        // Removed charges are NOT in balance
        const removal = adjustments.find(
            a => a.charge.name === charge.name && a.adjustmentType === 'remove'
        );

        return !removal;
    }

    async toggleChargeBalance(charge: Charge): Promise<void> {
        if (this.isChargeInBalance(charge)) {
            // → Arrow right → remove from balance
            await this.paymentPlanService.adjustCharge(charge, 'remove');
        } else {
            // ← Arrow left → restore original & re-add to balance
            await this.paymentPlanService.restoreCharge(charge);
        }
    }


    onContractToggleChange(contract: Contract, checked: boolean): void {
        const value = checked ? 'reactivation' : 'dunning';

        this.contractActivities.set(contract.identifier, value);
        this.onContractActionSelect(contract, value);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
